/**
 * Custom CSS cursors for the drawing transform interactions.
 *
 * One canonical SVG per cursor type (move, resize, rotate); per-corner
 * variants are derived by rotating the canonical icon. Each cursor's
 * SVG path is wrapped in an 18×18 inline-SVG document, encoded as a
 * `data:image/svg+xml` URI, and combined with a hotspot offset and a
 * native-cursor fallback into a single CSS `cursor:` value.
 *
 * Two ways to consume a cursor:
 *
 *   1. Static (at-rest) cursor strings on `DRAWING_CURSORS` — used
 *      for hover state on each handle. Pre-built at module load.
 *
 *   2. CSS custom properties installed on `<html>` once on first
 *      `installDrawingCursors()` call. SCSS rules read them via
 *      `cursor: var(--drawing-cursor-…, fallback)`.
 *
 *   3. `buildRotateCursor(corner, drawingRotation)` for the rotate
 *      gesture, where the cursor must follow the bbox's current
 *      angle in real time. Quantized + cached per stepped angle.
 *
 * Visual style: black fill + thick white halo on the canonical
 * SVG's native viewBox. The halo gives contrast against dark
 * backgrounds; the black fill carries the silhouette against
 * everything else. 18×18 keeps us under Safari's 32×32 cursor cap.
 */
/**
 * Canonical rotate icon. Drawn in its native ("NE corner") pose:
 * arrowheads at the top-left + bottom-right of a 25-unit square,
 * suggesting rotation around a pivot up + to the right of the
 * arc's centroid. Per-corner variants are derived by rotating
 * this icon via `CORNER_ROTATE_OFFSET`; the active rotate gesture
 * adds the drawing's current rotation on top.
 */
const ROTATE_ICON = {
    width: 25,
    height: 25,
    path: "M7.41984 2.23846C7.7378 1.92052 8.25343 1.92051 8.57138 2.23846C8.88934 2.55642 8.88933 3.07204 8.57138 3.39001L4.73715 7.22424C11.6549 7.53886 13.9473 9.51691 14.5379 10.0723C15.1418 10.6403 17.4217 13.0833 17.7728 20.2656L21.61 16.4286C21.928 16.1107 22.4436 16.1107 22.7615 16.4286C23.0795 16.7466 23.0795 17.2622 22.7615 17.5802L17.5799 22.7615C17.262 23.0795 16.7466 23.0795 16.4286 22.7615L11.247 17.5802C10.9291 17.2622 10.9291 16.7466 11.247 16.4286C11.565 16.1107 12.0806 16.1107 12.3986 16.4286L16.1369 20.167C15.7761 13.4898 13.6746 11.4958 13.4224 11.2586C13.1596 11.0114 11.2833 9.18088 4.82847 8.85852L8.57138 12.6014C8.88933 12.9194 8.88934 13.435 8.57138 13.753C8.25343 14.0709 7.7378 14.0709 7.41984 13.753L2.23847 8.57138C1.92051 8.25341 1.92051 7.73802 2.23847 7.42006L7.41984 2.23846Z",
};
/**
 * Canonical resize icon. Drawn as a double-headed diagonal arrow
 * along the NWSE axis (top-left ↔ bottom-right). Per-corner
 * variants are pure rotations:
 *   - NW + SE corners share the canonical NWSE orientation (0°)
 *   - NE + SW corners get a 90° rotation (NESW orientation)
 *
 * The resize cursor doesn't track the drawing's rotation — only
 * the corner determines orientation.
 */
const RESIZE_ICON = {
    width: 25,
    height: 25,
    path: "M20.7518 12.4999C21.2076 12.4999 21.5772 12.8695 21.5772 13.3253L21.577 20.7534C21.577 21.2092 21.2076 21.5787 20.7518 21.5787L13.3236 21.5788C12.8678 21.5788 12.4982 21.2092 12.4982 20.7534C12.4982 20.2976 12.8678 19.928 13.3236 19.928L18.7591 19.928L5.07027 6.23913L5.07026 11.6746C5.07025 12.1304 4.70066 12.5 4.24485 12.5C3.78903 12.5 3.41944 12.1304 3.41943 11.6746L3.41959 4.2465C3.41959 3.79067 3.78902 3.42124 4.24485 3.42124L11.673 3.42108C12.1288 3.42109 12.4984 3.79068 12.4984 4.2465C12.4984 4.70232 12.1288 5.07191 11.673 5.07192L6.23748 5.07192L19.9264 18.7608V13.3253C19.9264 12.8695 20.2959 12.4999 20.7518 12.4999Z",
};
/**
 * Canonical drag (move) icon. A four-way cross arrow — vertical
 * line + vertical arrowheads + horizontal line + horizontal
 * arrowheads, concatenated into one path-d (each `M` starts a
 * fresh subpath; the SVG renderer paints them as a single shape
 * so they share the halo). Rotationally symmetric, so no
 * per-corner orientation needed — applied to the selected
 * drawing's body wrapper as-is.
 */
const MOVE_ICON = {
    width: 25,
    height: 25,
    path: "M11.8896 22.7086L11.8452 2.81753C11.8444 2.46958 12.1258 2.18687 12.4738 2.18609C12.8217 2.18531 13.1044 2.46676 13.1052 2.81472L13.1497 22.7058C13.1504 23.0537 12.869 23.3364 12.521 23.3372C12.1731 23.338 11.8904 23.0565 11.8896 22.7086ZM12.0949 1.18161C12.3058 0.970714 12.6476 0.970714 12.8585 1.18161L16.2952 4.61832C16.5061 4.82921 16.5061 5.17106 16.2952 5.38196C16.0843 5.59285 15.7425 5.59285 15.5316 5.38196L13.0167 2.86709L13.0167 22.131L15.5316 19.6161C15.7425 19.4052 16.0843 19.4052 16.2952 19.6161C16.5061 19.827 16.5061 20.1688 16.2952 20.3797L12.8585 23.8164C12.6476 24.0273 12.3058 24.0273 12.0949 23.8164L8.65817 20.3797C8.44728 20.1688 8.44728 19.827 8.65817 19.6161C8.86907 19.4052 9.21091 19.4052 9.42181 19.6161L11.9367 22.131L11.9367 2.86709L9.42181 5.38196C9.21091 5.59285 8.86907 5.59285 8.65817 5.38196C8.44728 5.17106 8.44728 4.82921 8.65817 4.61832L12.0949 1.18161ZM22.7086 13.1084L2.81753 13.1529C2.46958 13.1537 2.18687 12.8722 2.18609 12.5243C2.18531 12.1763 2.46676 11.8936 2.81472 11.8928L22.7058 11.8484C23.0537 11.8476 23.3364 12.129 23.3372 12.477C23.338 12.825 23.0565 13.1077 22.7086 13.1084ZM1.18161 12.9032C0.970714 12.6923 0.970714 12.3504 1.18161 12.1395L4.61832 8.70282C4.82921 8.49192 5.17106 8.49192 5.38195 8.70282C5.59285 8.91371 5.59285 9.25556 5.38195 9.46645L2.86709 11.9813L22.131 11.9813L19.6161 9.46645C19.4052 9.25556 19.4052 8.91371 19.6161 8.70282C19.827 8.49192 20.1688 8.49192 20.3797 8.70282L23.8164 12.1395C24.0273 12.3504 24.0273 12.6923 23.8164 12.9032L20.3797 16.3399C20.1688 16.5508 19.827 16.5508 19.6161 16.3399C19.4052 16.129 19.4052 15.7871 19.6161 15.5762L22.131 13.0614L2.86709 13.0614L5.38196 15.5762C5.59285 15.7871 5.59285 16.129 5.38196 16.3399C5.17106 16.5508 4.82921 16.5508 4.61832 16.3399L1.18161 12.9032Z",
};
/**
 * Per-corner rotation (degrees, clockwise) applied to the canonical
 * rotate icon so each corner reads as "rotate around a pivot at
 * THIS corner of the bbox." NE is the canonical pose (0°); the
 * rest cycle clockwise around the bbox. Combined with the
 * drawing's current rotation in `buildRotateCursor` so the cursor
 * orientation always tracks the corner's CURRENT screen position,
 * not its bbox-local position.
 */
const CORNER_ROTATE_OFFSET = {
    ne: 0,
    se: 90,
    sw: 180,
    nw: 270,
};
/**
 * Per-corner rotation for the canonical resize icon (which points
 * along the NWSE diagonal at 0°). NW + SE share that diagonal, so
 * 0°. NE + SW share the orthogonal NESW diagonal, so 90°. As with
 * rotate, this offset is added to the drawing's current rotation
 * inside `buildResizeCursor` — a drawing rotated 45° turns its NW
 * handle's cursor 45° too, keeping the arrow visually inline with
 * the bbox's current diagonal.
 */
const CORNER_RESIZE_OFFSET = {
    nw: 0,
    se: 0,
    ne: 90,
    sw: 90,
};
/**
 * Native OS-cursor fallbacks per corner. Used only if the data URI
 * cursor fails to load — matches the corner's natural axis at
 * rotation 0 (the dynamic rotation argument doesn't affect what
 * the OS would have shown anyway).
 */
const CORNER_RESIZE_FALLBACK = {
    nw: "nwse-resize",
    se: "nwse-resize",
    ne: "nesw-resize",
    sw: "nesw-resize",
};
// Cursor canvas dimensions in screen pixels. 18px is 25% smaller
// than a typical OS cursor — easier on the eye when paired with
// the drawing's selection chrome, still safely under Safari's
// 32×32 cursor cap, and large enough that the icon shape stays
// readable.
const CURSOR_PX = 18;
// Hotspot at the cursor's center: the user's "click point" lines
// up with the icon's centroid, which feels right for symmetrical
// icons (move, resize, rotate).
const HOTSPOT = 9;
// Halo width in SCREEN pixels. Converted per-source into viewBox
// units inside `buildCursor` so a FortAwesome icon (512-unit
// viewBox) and a custom 25-unit SVG end up with the same physical
// halo thickness on screen.
const HALO_STROKE_PX = 2;
// Rotation quantization for the dynamic rotate cursor. 5° is small
// enough to feel continuous and large enough to bound the cache at
// 72 unique URLs (360 / 5) — both our local Map and the browser's
// underlying cursor bitmap cache hit on every step the user passes
// through more than once.
const ROTATION_STEP_DEG = 5;
/**
 * Build a CSS `cursor:` value from a generic cursor source.
 *
 * Returns a string of the form
 *   `url("data:image/svg+xml,…") 9 9, fallback`
 * — a complete `cursor` value the CSS engine will resolve, falling
 * back to the named OS cursor if for any reason the SVG can't load.
 *
 * @param source     `{ width, height, path }` — the icon's viewBox
 *                   dimensions plus its single path-d string.
 *                   Use `fromFaIcon()` to adapt a FortAwesome icon.
 * @param fallback   Native cursor name (`"move"`, `"nwse-resize"`,
 *                   `"grab"`).
 * @param rotateDeg  In-place rotation around the icon's center
 *                   (degrees, clockwise). Used to derive every
 *                   per-corner / per-rotation variant from the
 *                   canonical icon.
 */
function buildCursor(source, fallback, rotateDeg = 0) {
    const cx = source.width / 2;
    const cy = source.height / 2;
    // Convert the halo width from screen pixels into the source's
    // viewBox units so every icon ends up with the same physical
    // halo thickness on screen — independent of whether the source
    // is a 512-unit FortAwesome icon or a 25-unit custom SVG.
    const haloUnits = HALO_STROKE_PX * (source.width / CURSOR_PX);
    // Black fill carries the silhouette; white stroke gives a halo
    // for dark-background visibility. `paint-order: stroke fill`
    // ensures the stroke paints UNDER the fill so the visible shape
    // isn't smudged by the halo.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CURSOR_PX}" height="${CURSOR_PX}" viewBox="0 0 ${source.width} ${source.height}">` +
        `<path d="${source.path}" fill="#000" stroke="#fff" stroke-width="${haloUnits}"` +
        ` stroke-linejoin="round" paint-order="stroke fill"` +
        ` transform="rotate(${rotateDeg} ${cx} ${cy})"/></svg>`;
    // `encodeURIComponent` covers every reserved character in the
    // SVG body (`#`, `<`, quotes, etc.) so the resulting data URI is
    // safe inside a CSS `url("…")` no matter what the icon path
    // contains.
    const encoded = encodeURIComponent(svg);
    return `url("data:image/svg+xml;utf8,${encoded}") ${HOTSPOT} ${HOTSPOT}, ${fallback}`;
}
// ---------------------------------------------------------------------------
// Dynamic per-corner cursors
//
// Both the rotate and resize cursors compose two rotations:
//   1. A per-corner offset that orients the canonical icon for the
//      corner (e.g. NE corner → 0° for rotate, NW + SE → 0° for
//      resize).
//   2. The drawing's current rotation, so the cursor tracks which
//      corner the handle CURRENTLY occupies in screen space — not
//      its original bbox-local position. A drawing rotated 90°
//      shows its NW handle at the screen-NE position; the cursor
//      should match.
//
// Rotation is quantized to 5° steps before hitting the cache so a
// continuous rotate drag re-uses the same cursor URL for the same
// step — both this Map and the browser's underlying cursor bitmap
// cache hit on every step the user passes through more than once.
// ---------------------------------------------------------------------------
const dynamicRotateCache = new Map();
const dynamicResizeCache = new Map();
function quantizeAngle(deg) {
    const stepped = Math.round(deg / ROTATION_STEP_DEG) * ROTATION_STEP_DEG;
    // Normalize into [0, 360) so 360, 720, -180, etc. share cache
    // slots with their canonical equivalents.
    return ((stepped % 360) + 360) % 360;
}
/**
 * Build the rotate cursor for a given corner at the drawing's
 * current rotation. Result is a complete CSS `cursor:` value (URL
 * + hotspot + fallback). Cached per quantized total angle.
 */
export function buildRotateCursor(corner, drawingRotation) {
    const stepped = quantizeAngle(CORNER_ROTATE_OFFSET[corner] + drawingRotation);
    let cached = dynamicRotateCache.get(stepped);
    if (cached === undefined) {
        cached = buildCursor(ROTATE_ICON, "grab", stepped);
        dynamicRotateCache.set(stepped, cached);
    }
    return cached;
}
/**
 * Build the resize cursor for a given corner at the drawing's
 * current rotation. Result is a complete CSS `cursor:` value (URL
 * + hotspot + fallback). Cached per (quantized angle, fallback)
 * because NW/SE and NE/SW use different OS-cursor fallbacks even
 * though their data URIs may stop differing at certain rotations.
 */
export function buildResizeCursor(corner, drawingRotation) {
    const stepped = quantizeAngle(CORNER_RESIZE_OFFSET[corner] + drawingRotation);
    const fallback = CORNER_RESIZE_FALLBACK[corner];
    const key = `${stepped}|${fallback}`;
    let cached = dynamicResizeCache.get(key);
    if (cached === undefined) {
        cached = buildCursor(RESIZE_ICON, fallback, stepped);
        dynamicResizeCache.set(key, cached);
    }
    return cached;
}
/**
 * Pre-built static cursors that don't track the drawing's
 * rotation. Today: just `move` (a four-way arrow that's
 * rotationally symmetric — no need to spin it with the drawing).
 *
 * Per-corner rotate / resize cursors are intentionally NOT here —
 * they're computed inline from `frame.rotation` in
 * `DrawingSelectionView` so a drawing rotated mid-session always
 * shows correctly-oriented cursors on both hover and drag.
 */
export const DRAWING_CURSORS = {
    move: buildCursor(MOVE_ICON, "move"),
};
const CURSOR_VAR_NAMES = {
    move: "--drawing-cursor-move",
};
let installed = false;
/**
 * Install static cursor strings as CSS custom properties on the
 * document root. Today only the move cursor — corner-specific
 * cursors are applied inline in the chrome view because they
 * depend on `frame.rotation`. Idempotent.
 */
export function installDrawingCursors() {
    if (installed)
        return;
    if (typeof document === "undefined")
        return;
    installed = true;
    document.documentElement.style.setProperty(CURSOR_VAR_NAMES.move, DRAWING_CURSORS.move);
}
/**
 * Body class + CSS custom property used to globally force a
 * specific cursor while a gesture is active. The matching
 * `body.drawing-gesture-active, body.drawing-gesture-active *
 * { cursor: var(...) !important }` rule lives in
 * `annotation.scss` — it's `!important` because per-element
 * cursor rules would otherwise win on whichever element the
 * pointer happens to be over (e.g. when the pointer drifts off
 * the small handle mid-drag).
 *
 * One mechanism handles both the rotate gesture (cursor updated
 * per pointer-move as the drawing rotates) and the resize gesture
 * (cursor stays fixed throughout — drawing rotation doesn't
 * change during resize).
 */
export const GESTURE_ACTIVE_BODY_CLASS = "drawing-gesture-active";
export const GESTURE_ACTIVE_VAR = "--drawing-cursor-gesture-active";
